import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return "—";
  const [year, month, day] = d.split("T")[0].split("-");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function StatusBadge({ status }) {
  return <span className={`badge badge-${status}`}>{status}</span>;
}

export function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.getDashboard()
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading your account…</div>;
  if (error)   return <div className="alert alert-error">{error}</div>;

  const raw = data?.summary;
  const s = raw ? {
    ...raw,
    total_balance:    parseFloat(raw.total_balance) || 0,
    current_balance:  parseFloat(raw.current_balance) || 0,
    balance_30:       parseFloat(raw.balance_30) || 0,
    balance_60:       parseFloat(raw.balance_60) || 0,
    balance_90_plus:  parseFloat(raw.balance_90_plus) || 0,
    last_payment_amount: parseFloat(raw.last_payment_amount) || 0,
  } : null;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Dashboard</div>
        {data?.last_sync && (
          <div className="sync-notice">
            Last synced with QuickBooks: {fmtDate(data.last_sync.completed_at)}
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <div className="summary-label">Total Balance Due</div>
          <div className={`summary-value ${s?.total_balance > 0 ? "danger" : ""}`}>
            {fmt(s?.total_balance)}
          </div>
          <div className="summary-sub">{s?.open_invoice_count || 0} open invoice{s?.open_invoice_count !== 1 ? "s" : ""}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Current (0–30 days)</div>
          <div className="summary-value">{fmt(s?.current_balance)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Past Due (31–90 days)</div>
          <div className={`summary-value ${(s?.balance_30 + s?.balance_60) > 0 ? "warn" : ""}`}>
            {fmt((s?.balance_30 || 0) + (s?.balance_60 || 0))}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">90+ Days</div>
          <div className={`summary-value ${s?.balance_90_plus > 0 ? "danger" : ""}`}>
            {fmt(s?.balance_90_plus)}
          </div>
          {s?.last_payment_date && (
            <div className="summary-sub">Last payment: {fmtDate(s.last_payment_date)}</div>
          )}
        </div>
      </div>

      {/* Aging detail */}
      <div className="aging-grid" style={{ marginBottom: 32 }}>
        <div className="aging-bucket current">
          <div className="aging-bucket-label">Current</div>
          <div className="aging-bucket-amount">{fmt(s?.current_balance)}</div>
        </div>
        <div className="aging-bucket days30">
          <div className="aging-bucket-label">31–60 Days</div>
          <div className="aging-bucket-amount">{fmt(s?.balance_30)}</div>
        </div>
        <div className="aging-bucket days60">
          <div className="aging-bucket-label">61–90 Days</div>
          <div className="aging-bucket-amount">{fmt(s?.balance_60)}</div>
        </div>
        <div className="aging-bucket days90">
          <div className="aging-bucket-label">90+ Days</div>
          <div className="aging-bucket-amount">{fmt(s?.balance_90_plus)}</div>
        </div>
      </div>

      {/* Recent open invoices */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Open Invoices</span>
          <Link to="/invoices" className="btn btn-outline btn-sm">View all</Link>
        </div>
        {data?.recent_invoices?.length === 0
          ? (
            <div className="empty-state">
              <div className="empty-state-icon">✓</div>
              <p>No open invoices. You're all caught up.</p>
            </div>
          )
          : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th className="td-right">Amount</th>
                    <th className="td-right">Balance Due</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_invoices.map(inv => (
                    <tr key={inv.id}>
                      <td style={{ fontWeight: 500 }}>{inv.invoice_number || "—"}</td>
                      <td>{fmtDate(inv.invoice_date)}</td>
                      <td style={{ color: inv.status === "overdue" ? "var(--danger)" : undefined }}>
                        {fmtDate(inv.due_date)}
                      </td>
                      <td>
                        <StatusBadge status={inv.status} />
                        {inv.has_dispute && <span className="badge badge-overdue" style={{marginLeft:6,fontSize:"0.65rem"}}>Disputed</span>}
                        {inv.has_ticket_request && <span className="badge badge-partial" style={{marginLeft:6,fontSize:"0.65rem"}}>Tickets Requested</span>}
                      </td>
                      <td className="td-right td-mono">{fmt(inv.amount)}</td>
                      <td className="td-right td-mono" style={{ fontWeight: 600 }}>{fmt(inv.balance_due)}</td>
                      <td>
                        <Link to={`/invoices/${inv.id}`} className="btn btn-outline btn-sm">View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </div>
  );
}
